import {
  ArrayChip,
  FormLabel,
  NumberInput,
  PrimaryButton,
  SearchTextInput,
  TextInput,
} from "components";
import { Dialog, DialogActions, DialogContent } from "@material-ui/core";
import {
  Box,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid } from "@material-ui/data-grid";
import TwoTables from "../TableUnclaim";
import { Delete } from "@material-ui/icons";

import { globalStyles } from "styles";

const PopupConfirmUnclaim = ({
  modalVisible,
  onClose,
  items,
  onSubmit,
  isExistingSong,
  loadingButton,
  claimInput,
  handleClaimWorkcode,
  handleCheckSong,
}) => {
  const classes = globalStyles();
  const [query, setQuery] = useState("");
  const [filteredRows, setFilteredRows] = useState(items || []);
  const [deletedRowIds, setDeletedRowIds] = useState([]);
  useEffect(() => {
    const lowerQuery = query.toLowerCase();

    const filtered = items
      ?.filter(row => !deletedRowIds.includes(row.id)) // ❗Hapus yang dihapus
      ?.filter(
        row =>
          row.song_title?.toLowerCase().includes(lowerQuery) ||
          row.artist?.toLowerCase().includes(lowerQuery) ||
          row.dsp_name?.toLowerCase().includes(lowerQuery) ||
          (Array.isArray(row.composers) &&
            row.composers.some(c => c.toLowerCase().includes(lowerQuery)))
      );

    setFilteredRows(filtered);
  }, [query, items, deletedRowIds]); // ← Tambahkan `deletedRowIds` sebagai dependency

  const handleChangeQueryParams = value => {
    setQuery(value || "");
  };

  const handleCheckedSongPrimary = rowId => {
    const updated = filteredRows.map(row => ({
      ...row,
      is_primary_song: row.id === rowId, // hanya satu yang true
    }));
    setFilteredRows(updated);
  };

  const handleDeleteRow = rowId => {
    setDeletedRowIds(prev => [...prev, rowId]); // Simpan ID yang dihapus
    setFilteredRows(prevRows => prevRows.filter(row => row.id !== rowId));
    handleCheckSong(rowId);
  };

  const handleCheckedComposer = (rowId, index, newValue) => {
    const updated = filteredRows.map(row => {
      if (row.id !== rowId) return row;

      const updatedComposerData = [...row.composer_data];
      updatedComposerData[index].is_checked = newValue;

      return { ...row, composer_data: updatedComposerData };
    });

    setFilteredRows(updated);
  };

  const handleUpdateComposerData = (rowId, index, newValue) => {
    if (!/^\d{0,3}$/.test(newValue)) return;
    if (parseInt(newValue) > 100) return;

    setFilteredRows(prevRows =>
      prevRows.map(row => {
        if (row.id !== rowId) return row;

        const composerNameToUpdate = row?.composer_data?.[index]?.composer_name;

        const updatedComposerData = row.composer_data.map(composer => {
          if (composer.composer_name === composerNameToUpdate) {
            return {
              ...composer,
              ownership_percentage: Number(newValue),
              is_checked: true,
            };
          }
          return composer;
        });

        return { ...row, composer_data: updatedComposerData };
      })
    );
  };

  const handleClose = () => {
    setDeletedRowIds([]);
    onClose();
  };

  return (
    <Dialog open={modalVisible} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Process Unclaim</DialogTitle>
      <DialogContent>
        <Box mb="16px">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormLabel label="Search" />

              <SearchTextInput
                placeholder="Search"
                onChange={e => handleChangeQueryParams(e?.target?.value)}
              />
            </Grid>
            {!isExistingSong ? (
              <>
                <Grid item xs={12} sm={3}>
                  <FormLabel label="Input Workcode" />
                  <TextInput
                    value={claimInput?.workcode}
                    onChange={e =>
                      handleClaimWorkcode("workcode", e?.target?.value)
                    }
                    placeholder="Workcode"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormLabel label="Publisher Share (%)" />
                  <NumberInput
                    value={claimInput?.publisher_share_percentage}
                    onChange={value =>
                      handleClaimWorkcode(
                        "publisher_share_percentage",
                        Number(value)
                      )
                    }
                    endAdornment="%"
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </Box>

        {isExistingSong ? (
          <Paper sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columnsReference(classes, handleDeleteRow)}
              initialState={{ pagination: { paginationModel } }}
              pageSizeOptions={[5, 10]}
            />
          </Paper>
        ) : (
          <Paper sx={{ width: "100%" }}>
            <TwoTables
              data={filteredRows}
              handleDeleteRow={handleDeleteRow}
              handleCheckedSongPrimary={handleCheckedSongPrimary}
              handleUpdateComposerData={handleUpdateComposerData}
              handleCheckedComposer={handleCheckedComposer}
            />
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          label={`${loadingButton ? "Processing Claim ..." : "Confirm Claim"}`}
          onClick={() => onSubmit(filteredRows)}
          disabled={loadingButton}
          loading={loadingButton}
        />
      </DialogActions>
    </Dialog>
  );
};

const columnsReference = (classes, onDeleteRow) => [
  {
    field: "actions",
    headerName: "Actions",
    width: 100,
    sortable: false,
    disableColumnMenu: true,
    renderCell: params => (
      <Tooltip title="Delete row">
        <IconButton onClick={() => onDeleteRow(params.row.id)}>
          <Delete color="error" />
        </IconButton>
      </Tooltip>
    ),
  },
  {
    field: "song_title",
    headerName: "Song Title",
    width: 200,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "composers",
    headerName: "Composer/Author",
    width: 400,
    sortable: false,
    disableColumnMenu: true,
    renderCell: item => {
      return <ArrayChip list={item.value} max={2} />;
    },
  },
  {
    field: "all",
    headerName: "ISRC Code",
    width: 200,
    sortable: false,
    disableColumnMenu: true,
    renderCell: item => {
      return (
        <span className={classes.italicText}>
          <strong>{item.row?.isrc_code}</strong>
          {item.row?.alternate_isrc}
        </span>
      );
    },
  },
  {
    field: "artist",
    headerName: "Artist",
    width: 150,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: "dsp_name",
    headerName: "DSP",
    width: 150,
    sortable: false,
    disableColumnMenu: true,
  },
];

const paginationModel = { page: 0, pageSize: 5 };

export default PopupConfirmUnclaim;
